// SCR-STAFF-401 — صندوق (FLOW-05؛ US-BIL-030، 031، 033، 034، 061، 091، 100): انتخاب مراجعه‌کننده یا ورود از مراجعه (Alt+P ← /cashier/checkout/:visitId)،
// صورتحساب‌های باز، بیعانه پرداخت‌شده، کیف پول (خود و سرپرست خانواده)، ردیف‌های پرداخت با میانبر F1–F6 (مبلغ پیش‌فرض = باقیمانده)، اضافه دریافت به
// کیف پول یا برگشت نقد، بدهی با مجوز یا تأیید مدیر، لینک پرداخت آنلاین (نشان «نمونه» برای درگاه نمونه)، ثبت با Ctrl+Enter و چاپ رسید حرارتی.
// بدون شیفت باز کل صفحه دکمه «باز کردن شیفت» است (BR-BIL-040).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import {
  isApiError,
  unwrap,
  type BilCheckoutResponse,
  type BilOverpaymentHandling,
  type BilPaperSize,
  type BilPaymentLinkView,
  type BilPaymentMethod,
  type PatientSearchItem,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, faDigits, latinDigits, LoadingState, PageHeader, SampleBadge } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { useApproval } from '../iam/useApproval';
import { PatientPicker } from '../patients/PatientPicker';
import { ChequeDialog, PosPaymentButton } from './FinanceDialogs';
import {
  Amount,
  BIL_PRODUCT_SALE_PATH,
  BIL_SESSION_PATH,
  bilKeys,
  CASHIER_METHODS,
  financePath,
  invoicePath,
  InvoiceStatusChip,
  needsReference,
  num,
  rial,
  saveBlob,
  SCR_STAFF_401,
} from './common';

interface PaymentRow {
  id: number;
  method: BilPaymentMethod;
  amount: string;
  reference: string;
  cardLast4: string;
}

/** متن مبلغ ورودی (ارقام فارسی و جداکننده) ← ریال صحیح. */
export function parseRials(text: string): number {
  const latin = latinDigits(text).replace(/[^0-9]/g, '');
  return latin === '' ? 0 : Number(latin);
}

/** چاپ رسید (حرارتی پیش‌فرض) به PDF. */
export function useReceiptPrinter() {
  const { tenantApi } = useTenantSession();
  return async (receiptId: string, number: string, paper: Exclude<BilPaperSize, null> = 'Thermal80') => {
    const response = await tenantApi.GET('/api/v1/billing/receipts/{id}/print', {
      params: { path: { id: receiptId }, query: { format: paper, output: 'Pdf' } },
      parseAs: 'blob',
    });
    if (response.response.ok && response.data) {
      saveBlob(response.data as unknown as Blob, `${number}-${paper}.pdf`);
      return true;
    }
    return false;
  };
}

export function CashierPage() {
  const { t } = useTranslation('bil');
  const { visitId: routeVisitId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { tenantApi, session } = useTenantSession();
  const visit = useQuery({
    queryKey: bilKeys.visit(session?.tenantId, routeVisitId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/visits/{id}', { params: { path: { id: routeVisitId } } })),
    enabled: routeVisitId !== '',
  });
  const patientId = params.get('patientId') ?? visit.data?.patientId ?? '';
  const current = useQuery({ queryKey: bilKeys.session(session?.tenantId), queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cash-sessions/current')) });
  const [picked, setPicked] = useState<PatientSearchItem | null>(null);

  if (current.isPending || (routeVisitId !== '' && visit.isPending)) {
    return <LoadingState rows={6} />;
  }
  if (current.error) {
    return <ErrorState error={current.error} onRetry={() => void current.refetch()} />;
  }
  const shift = current.data?.session;

  return (
    <Stack data-screen-id={SCR_STAFF_401} spacing={2}>
      <PageHeader
        titleKey="bil:cashier.title"
        badges={
          shift ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              data-testid="bil-cashier-shift"
              label={t('bil:cashier.shift_open', { register: shift.registerName ?? '', cashier: shift.cashierName ?? '' })}
            />
          ) : undefined
        }
        actions={
          <Stack direction="row" spacing={1}>
            {shift ? (
              <Button component={RouterLink} to={BIL_PRODUCT_SALE_PATH} variant="outlined" data-testid="bil-cashier-products-link">
                {t('bil:products.title')}
              </Button>
            ) : null}
            <Button component={RouterLink} to={BIL_SESSION_PATH} variant="outlined" data-testid="bil-cashier-session-link">
              {t('bil:cashier.session')}
            </Button>
          </Stack>
        }
      />
      {!shift ? (
        <EmptyState
          titleKey="bil:cashier.no_shift"
          descriptionKey="bil:cashier.no_shift_help"
          action={
            <Button component={RouterLink} to={BIL_SESSION_PATH} variant="contained" data-testid="bil-cashier-open-shift">
              {t('bil:session.open')}
            </Button>
          }
        />
      ) : (
        <>
          {routeVisitId === '' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <PatientPicker
                labelKey="bil:cashier.patient"
                value={picked}
                onChange={(next) => {
                  setPicked(next);
                  if (next) {
                    setParams({ patientId: next.id });
                  }
                }}
                testId="bil-cashier-patient"
              />
            </Paper>
          )}
          {patientId !== '' ? (
            <Checkout key={`${patientId}-${routeVisitId}`} patientId={patientId} visitId={routeVisitId || undefined} />
          ) : null}
        </>
      )}
    </Stack>
  );
}

function Checkout({ patientId, visitId }: { patientId: string; visitId?: string }) {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const checkoutKey = useIdempotencyKey();
  const linkKey = useIdempotencyKey();
  const printReceipt = useReceiptPrinter();
  const { requireApproval, dialog: approvalDialog } = useApproval();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [overpayment, setOverpayment] = useState<Exclude<BilOverpaymentHandling, null>>('Wallet');
  const [allowDebt, setAllowDebt] = useState(false);
  const [useFamilyWallet, setUseFamilyWallet] = useState(false);
  const [result, setResult] = useState<BilCheckoutResponse | null>(null);
  const [link, setLink] = useState<BilPaymentLinkView | null>(null);
  const [printed, setPrinted] = useState<boolean | null>(null);
  const [chequeOpen, setChequeOpen] = useState(false);
  const nextId = useRef(1);
  const amountRefs = useRef(new Map<number, HTMLInputElement | null>());
  const context = useQuery({
    queryKey: bilKeys.cashier(session?.tenantId, patientId, visitId ?? ''),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/cashier/context', { params: { query: { patientId, visitId } } })),
  });
  const data = context.data;
  const balance = (data?.openInvoices ?? []).reduce((sum, invoice) => sum + invoice.balance.amount, 0);
  const paid = rows.reduce((sum, row) => sum + parseRials(row.amount), 0);
  const remaining = balance - paid;
  const wallet = useFamilyWallet && data?.payerWallet ? data.payerWallet : data?.wallet;
  const walletUsable = wallet?.usable.amount ?? 0;
  const hasCash = rows.some((row) => row.method === 'Cash');
  const allowed = new Set(data?.methods ?? []);
  const missingReference = rows.some((row) => needsReference(row.method) && row.reference.trim() === '');
  const debtBlocked = remaining > 0 && rows.length > 0 && !(data?.canAllowDebt && allowDebt);

  const checkout = useMutation({
    mutationFn: async () => {
      const payments = rows
        .filter((row) => parseRials(row.amount) > 0)
        .map((row) => ({
          method: row.method,
          amount: rial(parseRials(row.amount)),
          reference: row.reference.trim() || null,
          cardLast4: row.cardLast4.trim() || null,
        }));
      const body = {
        patientId,
        visitId: visitId ?? data?.visitId ?? null,
        invoiceIds: null,
        payments,
        overpayment: remaining < 0 ? overpayment : null,
        closeVisit: true,
        allowDebt: remaining > 0 && allowDebt,
        approvalId: null as string | null,
        walletOwnerId: useFamilyWallet ? (data?.payerWalletOwnerId ?? null) : null,
      };
      const send = () =>
        unwrap(tenantApi.POST('/api/v1/billing/cashier/checkouts', { body, headers: { 'Idempotency-Key': checkoutKey.current() } }));
      try {
        return await send();
      } catch (error) {
        // PHX-BIL-0209 (C-AGENT-554): بدهی بیش از سقف ← تأیید مدیر حاضر یا درخواست و تکرار با approvalId.
        if (isApiError(error) && error.code === 'PHX-BIL-0209' && typeof error.extensions['key'] === 'string') {
          checkoutKey.settle(error);
          const outcome = await requireApproval({ key: error.extensions['key'] as string, amount: num(error.extensions['amount'] as number), context: { patientId } });
          if (outcome.status === 'approved' || outcome.status === 'requested') {
            body.approvalId = outcome.approvalId;
            return send();
          }
        }
        throw error;
      }
    },
    onSettled: (_data, error) => checkoutKey.settle(error ?? undefined),
    onSuccess: async (response) => {
      setResult(response);
      setRows([]);
      void queryClient.invalidateQueries({ queryKey: bilKeys.all(session?.tenantId) });
      if (response.receiptId && response.receiptNumber) {
        setPrinted(await printReceipt(response.receiptId, response.receiptNumber));
      }
    },
  });

  const createLink = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/payment-links', {
          body: { patientId, invoiceIds: null, amount: remaining > 0 ? rial(remaining) : null },
          headers: { 'Idempotency-Key': linkKey.current() },
        }),
      ),
    onSettled: (_data, error) => linkKey.settle(error ?? undefined),
    onSuccess: setLink,
  });

  const addMethod = (method: BilPaymentMethod) => {
    if (method === 'OnlineGateway') {
      if (data?.onlineAvailable && remaining > 0 && !createLink.isPending) {
        createLink.mutate();
      }
      return;
    }
    if (!allowed.has(method)) {
      return;
    }
    const preset = method === 'Wallet' ? Math.min(Math.max(remaining, 0), walletUsable) : Math.max(remaining, 0);
    const id = nextId.current++;
    setResult(null);
    setRows((current) => [...current, { id, method, amount: preset > 0 ? String(preset) : '', reference: '', cardLast4: '' }]);
    setTimeout(() => amountRefs.current.get(id)?.focus(), 0);
  };
  const update = (id: number, patch: Partial<PaymentRow>) => setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  const canSubmit = rows.length > 0 && paid > 0 && !missingReference && !debtBlocked && !checkout.isPending && access.accessMode === 'Full';

  // میانبرهای FLOW-05: F1..F6 انتخاب روش، Ctrl+Enter ثبت نهایی.
  const shortcuts = useRef({ add: addMethod, submit: () => {} });
  shortcuts.current = { add: addMethod, submit: () => canSubmit && checkout.mutate() };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const entry = CASHIER_METHODS.find((item) => item.key === event.key);
      if (entry) {
        event.preventDefault();
        shortcuts.current.add(entry.method);
      } else if (event.ctrlKey && event.key === 'Enter') {
        event.preventDefault();
        shortcuts.current.submit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (context.isPending) {
    return <LoadingState rows={4} />;
  }
  if (context.error || !data) {
    return <ErrorState error={context.error} onRetry={() => void context.refetch()} />;
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
          <Stack spacing={0.5}>
            <Typography variant="h6" data-testid="bil-cashier-patient-name">
              {data.patientName ?? '—'}
            </Typography>
            <Link component={RouterLink} to={financePath(patientId)} variant="body2">
              {t('bil:cashier.finance')}
            </Link>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Stack>
              <Typography variant="caption">{t('bil:cashier.wallet')}</Typography>
              <Amount money={data.wallet.usable} testId="bil-cashier-wallet" />
              {data.wallet.gift.amount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t('bil:cashier.wallet_gift')} <Amount money={data.wallet.gift} />
                </Typography>
              )}
            </Stack>
            <Stack>
              <Typography variant="caption">{t('bil:cashier.debt')}</Typography>
              <Amount money={data.debt} testId="bil-cashier-debt" />
            </Stack>
            <Stack>
              <Typography variant="caption">{t('bil:cashier.credit')}</Typography>
              <Amount money={data.refundableCredit} />
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('bil:cashier.open_invoices')}
        </Typography>
        {data.openInvoices.length === 0 ? (
          <Typography color="text.secondary" data-testid="bil-cashier-no-invoices">
            {t('bil:cashier.no_invoices')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            {data.openInvoices.map((invoice) => (
              <Stack key={invoice.id} direction="row" spacing={2} alignItems="center" data-testid="bil-cashier-invoice" data-invoice-id={invoice.id}>
                <Link component={RouterLink} to={invoicePath(invoice.id)}>
                  <bdi dir="ltr">{invoice.number}</bdi>
                </Link>
                <InvoiceStatusChip status={invoice.status} />
                <Typography variant="body2">{t('bil:cashier.payable')}</Typography>
                <Amount money={invoice.payable} />
                <Typography variant="body2">{t('bil:cashier.balance')}</Typography>
                <Amount money={invoice.balance} />
              </Stack>
            ))}
            {data.deposits.length > 0 && (
              <Typography variant="body2" color="text.secondary" data-testid="bil-cashier-deposits">
                {t('bil:cashier.deposits_paid', { n: data.deposits.length })}
              </Typography>
            )}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            {CASHIER_METHODS.map(({ method, key }) => {
              const enabled = method === 'OnlineGateway' ? data.onlineAvailable && allowed.has(method) : allowed.has(method) && (method !== 'Wallet' || walletUsable > 0);
              return (
                <Button
                  key={method}
                  variant="outlined"
                  onClick={() => addMethod(method)}
                  disabled={!enabled || (method !== 'OnlineGateway' && remaining <= 0 && rows.length > 0)}
                  data-testid={`bil-method-${method}`}
                >
                  {t(`bil:method.${method}`)} {key}
                </Button>
              );
            })}
            {data.onlineIsSample && data.onlineAvailable ? <SampleBadge /> : null}
            {access.can('bil.cheque.manage') && access.accessMode === 'Full' && remaining > 0 && data.openInvoices.length > 0 ? (
              <Button variant="outlined" onClick={() => setChequeOpen(true)} data-testid="bil-cashier-cheque">
                {t('bil:cheques.register')}
              </Button>
            ) : null}
          </Stack>
          {allowed.has('CardPos') && access.accessMode === 'Full' && rows.length === 0 && remaining > 0 ? (
            <PosPaymentButton patientId={patientId} amount={remaining} onDone={() => void context.refetch()} />
          ) : null}
          {data.payerWallet && data.payerWalletOwnerId ? (
            <FormControlLabel
              control={<Checkbox checked={useFamilyWallet} onChange={(event) => setUseFamilyWallet(event.target.checked)} />}
              label={
                <span>
                  {t('bil:cashier.family_wallet')} <Amount money={data.payerWallet.usable} />
                </span>
              }
              data-testid="bil-cashier-family-wallet"
            />
          ) : null}
          {rows.map((row) => (
            <Stack key={row.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} data-testid="bil-payment-row" data-method={row.method}>
              <Typography sx={{ minWidth: 110 }}>{t(`bil:method.${row.method}`)}</Typography>
              <TextField
                size="small"
                label={t('bil:cashier.amount')}
                value={row.amount}
                onChange={(event) => update(row.id, { amount: event.target.value })}
                inputRef={(element: HTMLInputElement | null) => amountRefs.current.set(row.id, element)}
                slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'bil-payment-amount' } }}
                helperText={row.method === 'Wallet' ? t('bil:cashier.wallet_available', { amount: faDigits(walletUsable.toLocaleString('en-US')) }) : undefined}
              />
              {needsReference(row.method) && (
                <TextField
                  size="small"
                  required
                  label={t(row.method === 'CardPos' ? 'bil:cashier.pos_reference' : 'bil:cashier.transfer_reference')}
                  value={row.reference}
                  onChange={(event) => update(row.id, { reference: event.target.value })}
                  slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'bil-payment-reference' } }}
                />
              )}
              {row.method === 'CardPos' && (
                <TextField
                  size="small"
                  label={t('bil:cashier.card_last4')}
                  value={row.cardLast4}
                  onChange={(event) => update(row.id, { cardLast4: event.target.value.replace(/\D/g, '').slice(0, 4) })}
                  slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', maxLength: 4 } }}
                  sx={{ maxWidth: 120 }}
                />
              )}
              <IconButton aria-label={t('bil:cashier.remove_row')} onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}>
                <CloseIcon />
              </IconButton>
            </Stack>
          ))}
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography>{remaining >= 0 ? t('bil:cashier.remaining') : t('bil:cashier.overpaid')}</Typography>
            <Amount money={rial(Math.abs(remaining))} testId="bil-cashier-remaining" />
          </Stack>
          {remaining < 0 && (
            <RadioGroup row value={overpayment} onChange={(event) => setOverpayment(event.target.value as 'Wallet' | 'ReturnCash')} data-testid="bil-overpayment">
              <FormControlLabel value="Wallet" control={<Radio />} label={t('bil:cashier.overpay_wallet')} />
              <FormControlLabel value="ReturnCash" control={<Radio />} label={t('bil:cashier.overpay_cash')} disabled={!hasCash} />
            </RadioGroup>
          )}
          {remaining > 0 && rows.length > 0 && (
            data.canAllowDebt ? (
              <FormControlLabel
                control={<Checkbox checked={allowDebt} onChange={(event) => setAllowDebt(event.target.checked)} data-testid="bil-cashier-allow-debt" />}
                label={t('bil:cashier.allow_debt')}
              />
            ) : (
              <Alert severity="warning" data-testid="bil-cashier-debt-blocked">
                {t('bil:cashier.debt_not_allowed')}
              </Alert>
            )
          )}
          {link && (
            <Alert severity="info" data-testid="bil-cashier-link" action={link.isSample ? <SampleBadge /> : undefined}>
              {t('bil:cashier.link_created')} <bdi dir="ltr">{link.url}</bdi>
            </Alert>
          )}
          {createLink.error ? <ErrorState error={createLink.error} compact /> : null}
          {checkout.error ? <ErrorState error={checkout.error} compact /> : null}
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={() => checkout.mutate()} disabled={!canSubmit} data-testid="bil-checkout-submit">
              {t('bil:cashier.submit')} (Ctrl+Enter)
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {result && (
        <Alert
          severity="success"
          data-testid="bil-checkout-result"
          action={
            result.receiptId && result.receiptNumber ? (
              <Button color="inherit" onClick={() => void printReceipt(result.receiptId!, result.receiptNumber!, 'A5')}>
                {t('bil:cashier.print_a5')}
              </Button>
            ) : undefined
          }
        >
          {t('bil:cashier.done', { number: result.receiptNumber ?? '' })}
          {result.walletCredited.amount > 0 ? ` · ${t('bil:cashier.wallet_credited')}` : ''}
          {result.changeReturned.amount > 0 ? ` · ${t('bil:cashier.change_returned')}` : ''}
          {printed === false ? ` · ${t('bil:cashier.print_failed')}` : ''}
        </Alert>
      )}
      {result && (
        <Button sx={{ alignSelf: 'flex-start' }} onClick={() => void navigate('/cashier')} data-testid="bil-cashier-next">
          {t('bil:cashier.next')}
        </Button>
      )}
      {chequeOpen ? (
        <ChequeDialog
          open
          patientId={patientId}
          amount={remaining}
          invoiceIds={data.openInvoices.map((invoice) => invoice.id)}
          onClose={() => setChequeOpen(false)}
          onDone={() => void context.refetch()}
        />
      ) : null}
      {approvalDialog}
    </Stack>
  );
}
